<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import VariableTextArea from './VariableTextArea.vue';
import FileAudienceUploader from './FileAudienceUploader.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
  },
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const store = useStore();
const AUTO_VARIATIONS_COUNT = 6;

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getInboxes'), // Usaremos todos los inboxes y filtraremos
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  excludedAudience: [],
  matchType: 'any', // any | all | exact
  labelType: 'conversation', // 'conversation' o 'contact'
  audienceSource: 'labels', // 'labels' | 'file'
  fileAudience: [],
  imageFile: null,
  imagePreview: null,
  messageConfirmed: false,
  generatedVariations: [],
  autoVariationFallback: false,
};

const state = reactive({ ...initialState });

const isFileAudience = computed(() => state.audienceSource === 'file');

const audienceRequirementMet = value =>
  state.audienceSource === 'file'
    ? state.fileAudience.length > 0
    : Array.isArray(value) && value.length > 0;

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { audienceRequirementMet },
  labelType: { required },
};

const v$ = useVuelidate(rules, state);
const isHydrating = ref(false);

const isCreating = computed(() => formState.uiFlags.value.isCreating);
const isUpdating = computed(() => formState.uiFlags.value.isUpdating);
const isEditing = computed(() => props.mode === 'edit' && !!props.campaign?.id);
const isCloneMode = computed(
  () => props.mode === 'clone' && !!props.campaign?.id
);
const isSubmitting = computed(() =>
  isEditing.value ? isUpdating.value : isCreating.value
);
const isGeneratingVariations = computed(
  () => formState.uiFlags.value.isGeneratingVariations
);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

// Filtrar solo inboxes de Evolution API (Channel::Api con webhook)
const evolutionAPIInboxes = computed(() => {
  return formState.inboxes.value.filter(inbox => {
    return (
      inbox.channel_type === 'Channel::Api' &&
      inbox.webhook_url &&
      inbox.webhook_url.includes('/chatwoot/webhook/')
    );
  });
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() => {
  // Usar las mismas etiquetas para ambos tipos (conversation y contact)
  const sourceData = formState.labels.value || [];

  // Usar la función de mapeo estándar para ambos casos
  const options = mapToOptions(sourceData, 'id', 'title');

  return options;
});

const normalizeId = id => String(id);

const includeAudienceOptions = computed(() => {
  const excludedSet = new Set(state.excludedAudience.map(normalizeId));
  return audienceList.value.filter(
    option => !excludedSet.has(normalizeId(option.value))
  );
});

const excludedAudienceOptions = computed(() => {
  const selectedSet = new Set(state.selectedAudience.map(normalizeId));
  return audienceList.value.filter(
    option => !selectedSet.has(normalizeId(option.value))
  );
});

const handleSelectedAudienceUpdate = values => {
  const nextSelected = Array.isArray(values) ? [...values] : [];
  const selectedSet = new Set(nextSelected.map(normalizeId));

  state.selectedAudience = nextSelected;
  state.excludedAudience = state.excludedAudience.filter(
    id => !selectedSet.has(normalizeId(id))
  );
};

const handleExcludedAudienceUpdate = values => {
  const nextExcluded = Array.isArray(values) ? [...values] : [];
  const selectedSet = new Set(state.selectedAudience.map(normalizeId));

  // Include tiene prioridad: una etiqueta ya incluida no puede agregarse a excluir.
  state.excludedAudience = nextExcluded.filter(
    id => !selectedSet.has(normalizeId(id))
  );
};

const matchModeEntity = computed(() =>
  t(
    `CAMPAIGN.QRBUHO.MATCH_MODE_ENTITY.${
      state.labelType === 'conversation' ? 'CONVERSATIONS' : 'CONTACTS'
    }`
  )
);

const matchModeDescription = computed(() =>
  t(`CAMPAIGN.QRBUHO.MATCH_MODE_HELP.${state.matchType.toUpperCase()}`, {
    entity: matchModeEntity.value,
  })
);

const excludedAudienceDescription = computed(() =>
  t('CAMPAIGN.QRBUHO.EXCLUDED_AUDIENCE.HELP', {
    entity: matchModeEntity.value,
  })
);

const inboxOptions = computed(() =>
  mapToOptions(evolutionAPIInboxes.value, 'id', 'name')
);

const getErrorMessage = field => {
  if (v$.value[field].$error) {
    switch (field) {
      case 'title':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.ERROR');
      case 'message':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.MESSAGE.ERROR');
      case 'inboxId':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.INBOX.ERROR');
      case 'scheduledAt':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.SCHEDULED_AT.ERROR');
      case 'selectedAudience':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.AUDIENCE.ERROR');
      default:
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.ERROR');
    }
  }
  return '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title'),
  message: getErrorMessage('message'),
  inbox: getErrorMessage('inboxId'),
  scheduledAt: getErrorMessage('scheduledAt'),
  audience: getErrorMessage('selectedAudience'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const submitButtonLabel = computed(() => {
  if (isEditing.value) {
    return t('CAMPAIGN.QRBUHO.EDIT.SAVE_BUTTON_TEXT');
  }

  if (isCloneMode.value) {
    return t('CAMPAIGN.QRBUHO.CLONE.CLONE_BUTTON_TEXT');
  }

  return t('CAMPAIGN.QRBUHO.CREATE.CREATE_BUTTON_TEXT');
});

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const formatFromUnixToLocalDateTime = unixTimestamp => {
  if (!unixTimestamp) return null;

  const date = new Date(unixTimestamp * 1000);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const updateStateFromCampaign = campaign => {
  if (!campaign) {
    resetState();
    return;
  }

  const audienceFilter = campaign.template_params?.audience_filter || {};
  const scope = audienceFilter.scope || null;
  const includeIds = Array.isArray(audienceFilter.include_ids)
    ? audienceFilter.include_ids
    : [];
  const excludeIds = Array.isArray(audienceFilter.exclude_ids)
    ? audienceFilter.exclude_ids
    : [];

  const fileAudience = Array.isArray(campaign.template_params?.file_audience)
    ? campaign.template_params.file_audience.filter(
        entry => entry?.number && entry?.name
      )
    : [];
  const audienceSource = fileAudience.length > 0 ? 'file' : 'labels';

  const legacyConversationIds = Array.isArray(campaign.audience)
    ? campaign.audience
        .filter(item => item.type === 'Label')
        .map(item => Number(item.id))
    : [];
  const legacyContactIds = Array.isArray(campaign.audience)
    ? campaign.audience
        .filter(item => item.type === 'ContactTag')
        .map(item => Number(item.id))
    : [];

  const resolvedLabelType =
    scope || (legacyContactIds.length ? 'contact' : 'conversation');
  let resolvedSelectedAudience = includeIds.map(Number);
  if (!resolvedSelectedAudience.length) {
    resolvedSelectedAudience =
      resolvedLabelType === 'contact'
        ? legacyContactIds
        : legacyConversationIds;
  }

  isHydrating.value = true;
  Object.assign(state, {
    title: campaign.title || '',
    message: campaign.message || '',
    inboxId: campaign.inbox?.id || null,
    scheduledAt: isCloneMode.value
      ? null
      : formatFromUnixToLocalDateTime(campaign.scheduled_at),
    labelType: resolvedLabelType,
    selectedAudience: resolvedSelectedAudience,
    excludedAudience: excludeIds.map(Number),
    matchType: audienceFilter.match_type || 'any',
    audienceSource,
    fileAudience,
    imageFile: null,
    imagePreview: null,
    messageConfirmed: true,
    generatedVariations: Array.isArray(
      campaign.template_params?.message_variations
    )
      ? campaign.template_params.message_variations
      : [],
    autoVariationFallback: false,
  });
  isHydrating.value = false;
  v$.value.$reset();
};

const validateImageFile = file => {
  const validTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  const maxSize = 5 * 1024 * 1024; // 5MB en bytes

  if (!validTypes.includes(file.type)) {
    return t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.ERROR.INVALID_TYPE');
  }

  if (file.size > maxSize) {
    return t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.ERROR.FILE_TOO_LARGE');
  }

  return null;
};

const handleImageUpload = event => {
  const file = event.target.files[0];
  if (!file) return;

  const error = validateImageFile(file);
  if (error) {
    // TODO: Mostrar notificación de error al usuario
    // Limpiar el input
    event.target.value = '';
    return;
  }

  state.imageFile = file;

  // Crear preview
  const reader = new FileReader();
  reader.onload = e => {
    state.imagePreview = e.target.result;
  };
  reader.readAsDataURL(file);
};

const removeImage = () => {
  state.imageFile = null;
  state.imagePreview = null;
};

const generateVariationsAutomatically = async () => {
  if (!state.message?.trim()) {
    state.generatedVariations = [];
    state.autoVariationFallback = false;
    return;
  }

  try {
    const response = await store.dispatch('campaigns/generateVariations', {
      message: state.message,
      count: AUTO_VARIATIONS_COUNT,
    });

    const variations = Array.isArray(response?.variations)
      ? response.variations
      : [];

    if (variations.length) {
      state.generatedVariations = variations;
      state.autoVariationFallback = false;
      return;
    }

    state.generatedVariations = [];
    state.autoVariationFallback = true;
  } catch {
    // Si IA falla por config, cuota o timeout, continuamos con mensaje base.
    state.generatedVariations = [];
    state.autoVariationFallback = true;
  }
};

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  if (isGeneratingVariations.value) {
    useAlert(t('CAMPAIGN.QRBUHO.CREATE.FORM.VARIATIONS.GENERATING'));
    return;
  }

  const result = await v$.value.$validate();
  if (!result) return;

  if (!state.messageConfirmed) {
    useAlert(t('CAMPAIGN.QRBUHO.CREATE.FORM.VARIATIONS.CONFIRM_REQUIRED'));
    return;
  }

  const includeIds = [...new Set(state.selectedAudience.map(Number))];
  const excludeIds = [...new Set(state.excludedAudience.map(Number))].filter(
    id => !includeIds.includes(id)
  );

  const usingFileAudience = isFileAudience.value;

  const campaignData = {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    campaign_type: 'one_off',
    audience: usingFileAudience
      ? []
      : state.selectedAudience.map(audienceId => ({
          type: state.labelType === 'conversation' ? 'Label' : 'ContactTag',
          id: audienceId,
        })),
    template_params: {
      audience_filter: usingFileAudience
        ? null
        : {
            scope: state.labelType,
            match_type: state.matchType,
            include_ids: includeIds,
            exclude_ids: excludeIds,
          },
      file_audience: usingFileAudience
        ? state.fileAudience.map(({ number, name }) => ({ number, name }))
        : [],
      message_variations: state.generatedVariations,
    },
  };

  // Usar FormData para enviar archivos correctamente
  const formData = new FormData();
  formData.append('campaign', JSON.stringify(campaignData));

  // Agregar imagen si existe
  if (state.imageFile) {
    formData.append('image', state.imageFile);
  }

  emit('submit', formData);
};

// Watcher para limpiar la audiencia seleccionada cuando cambie el tipo
watch(
  () => state.labelType,
  (newType, oldType) => {
    if (isHydrating.value) {
      return;
    }

    if (newType !== oldType) {
      // Limpiar la audiencia seleccionada cuando cambie el tipo
      state.selectedAudience = [];
      state.excludedAudience = [];
    }
  }
);

watch(
  () => state.matchType,
  newMatchType => {
    if (isHydrating.value) {
      return;
    }

    // Excluir no aplica en coincidencia exacta: la regla ya descarta etiquetas extra
    if (newMatchType === 'exact' && state.excludedAudience.length) {
      state.excludedAudience = [];
    }
  }
);

watch(
  () => state.audienceSource,
  newSource => {
    if (isHydrating.value) {
      return;
    }

    if (newSource === 'file') {
      state.selectedAudience = [];
      state.excludedAudience = [];
    } else {
      state.fileAudience = [];
    }
  }
);

watch(
  () => state.message,
  (newMessage, oldMessage) => {
    if (isHydrating.value) {
      return;
    }

    if (newMessage !== oldMessage) {
      if (state.messageConfirmed) {
        state.messageConfirmed = false;
      }

      state.generatedVariations = [];
      state.autoVariationFallback = false;
    }
  }
);

watch(
  () => state.messageConfirmed,
  confirmed => {
    if (isHydrating.value) {
      return;
    }

    if (confirmed) {
      generateVariationsAutomatically();
    } else {
      state.generatedVariations = [];
      state.autoVariationFallback = false;
    }
  }
);

// Las etiquetas se cargan automáticamente por el store cuando se necesitan

watch(
  () => [props.mode, props.campaign],
  ([mode, campaign]) => {
    if ((mode === 'edit' || mode === 'clone') && campaign) {
      updateStateFromCampaign(campaign);
      return;
    }

    resetState();
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Título -->
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <!-- Mensaje -->
    <VariableTextArea
      v-model="state.message"
      :label="t('CAMPAIGN.QRBUHO.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
      :max-length="2000"
      show-character-count
    />

    <!-- Variaciones IA -->
    <div
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-alpha-6 bg-n-alpha-2"
    >
      <label
        class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        <input
          v-model="state.messageConfirmed"
          type="checkbox"
          class="rounded border-n-alpha-6"
        />
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.VARIATIONS.CONFIRM_LABEL') }}
      </label>

      <p class="text-xs text-n-slate-9">
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.VARIATIONS.AUTO_HELP') }}
      </p>

      <div v-if="state.messageConfirmed" class="flex flex-col gap-2">
        <p v-if="isGeneratingVariations" class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.VARIATIONS.GENERATING') }}
        </p>

        <p
          v-if="state.autoVariationFallback && !isGeneratingVariations"
          class="text-xs text-n-slate-10"
        >
          {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.VARIATIONS.FALLBACK_MESSAGE') }}
        </p>

        <div
          v-if="state.generatedVariations.length"
          class="max-h-56 overflow-y-auto rounded-md border border-n-alpha-6 bg-n-alpha-1 p-2"
        >
          <div class="flex flex-col gap-2">
            <div
              v-for="(variation, index) in state.generatedVariations"
              :key="`${index}-${variation.slice(0, 20)}`"
              class="rounded-lg border border-n-alpha-5 bg-n-alpha-2 p-2"
            >
              <p
                class="text-[11px] uppercase tracking-wide text-n-slate-9 mb-1"
              >
                {{
                  t('CAMPAIGN.QRBUHO.CREATE.FORM.VARIATIONS.CARD_TITLE', {
                    number: index + 1,
                  })
                }}
              </p>
              <p class="text-xs text-n-slate-11 leading-5 whitespace-pre-wrap">
                {{ variation }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Imagen -->
    <div class="flex flex-col gap-3">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.LABEL') }}
      </label>

      <!-- Upload de imagen o preview -->
      <div v-if="!state.imagePreview" class="flex flex-col gap-2">
        <label class="w-full">
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            class="hidden"
            @change="handleImageUpload"
          />
          <div
            class="w-full justify-center py-3 border-2 border-dashed border-n-alpha-6 hover:border-n-alpha-8 bg-n-alpha-2 hover:bg-n-alpha-3 cursor-pointer rounded-md flex items-center text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
          >
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.ADD_BUTTON') }}
          </div>
        </label>
        <p class="text-xs text-n-slate-9 text-center">
          {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.HELP_TEXT') }}
        </p>
      </div>

      <!-- Preview de imagen -->
      <div v-else class="relative">
        <div class="rounded-lg border border-n-alpha-6 p-3 bg-n-alpha-2">
          <div class="flex items-start gap-3">
            <img
              :src="state.imagePreview"
              :alt="state.imageFile?.name"
              class="w-20 h-20 object-cover rounded-md border border-n-alpha-6"
            />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ state.imageFile?.name }}
              </p>
              <p class="text-xs text-n-slate-9">
                {{ (state.imageFile?.size / 1024 / 1024).toFixed(2) }}
                {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.SIZE_MB') }}
              </p>
            </div>
            <button
              type="button"
              class="p-1 rounded-md hover:bg-n-alpha-4 text-n-slate-9 hover:text-n-slate-12 transition-colors"
              @click.stop.prevent="removeImage"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Inbox Selection -->
    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Fuente de audiencia -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.AUDIENCE_SOURCE.LABEL') }}
      </label>
      <div class="flex gap-0 rounded-lg overflow-hidden p-1 bg-n-alpha-1">
        <button
          type="button"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
          :class="[
            state.audienceSource === 'labels'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.audienceSource = 'labels'"
        >
          {{ t('CAMPAIGN.QRBUHO.AUDIENCE_SOURCE.LABELS') }}
        </button>
        <button
          type="button"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
          :class="[
            state.audienceSource === 'file'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.audienceSource = 'file'"
        >
          {{ t('CAMPAIGN.QRBUHO.AUDIENCE_SOURCE.FILE') }}
        </button>
      </div>
    </div>

    <!-- Audiencia por archivo -->
    <FileAudienceUploader
      v-if="isFileAudience"
      :model-value="state.fileAudience"
      @update:model-value="value => (state.fileAudience = value)"
    />

    <!-- Tipo de etiqueta -->
    <div v-if="!isFileAudience" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.LABEL_TYPE') }}
      </label>
      <div class="flex gap-0 rounded-lg overflow-hidden p-1 bg-n-alpha-1">
        <button
          type="button"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
          :class="[
            state.labelType === 'conversation'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.labelType = 'conversation'"
        >
          {{ t('CAMPAIGN.QRBUHO.CONVERSATION') }}
        </button>
        <button
          type="button"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
          :class="[
            state.labelType === 'contact'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.labelType = 'contact'"
        >
          {{ t('CAMPAIGN.QRBUHO.CONTACT') }}
        </button>
      </div>
    </div>

    <!-- Incluir etiquetas -->
    <div v-if="!isFileAudience" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.INCLUDE_AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        id="audience"
        :model-value="state.selectedAudience"
        :options="includeAudienceOptions"
        :has-error="!!formErrors.audience"
        :placeholder="
          t('CAMPAIGN.QRBUHO.CREATE.FORM.INCLUDE_AUDIENCE.PLACEHOLDER')
        "
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="handleSelectedAudienceUpdate"
      />
    </div>

    <!-- Regla de coincidencia -->
    <div v-if="!isFileAudience" class="flex flex-col gap-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.MATCH_MODE') }}
      </label>
      <div class="flex gap-0 rounded-lg overflow-hidden p-1 bg-n-alpha-1">
        <button
          type="button"
          class="flex-1 px-3 py-2 text-xs font-medium leading-tight text-center rounded-md transition-all duration-200"
          :class="[
            state.matchType === 'any'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.matchType = 'any'"
        >
          {{ t('CAMPAIGN.QRBUHO.MATCH_MODE_OPTIONS.ANY') }}
        </button>
        <button
          type="button"
          class="flex-1 px-3 py-2 text-xs font-medium leading-tight text-center rounded-md transition-all duration-200"
          :class="[
            state.matchType === 'all'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.matchType = 'all'"
        >
          {{ t('CAMPAIGN.QRBUHO.MATCH_MODE_OPTIONS.ALL') }}
        </button>
        <button
          type="button"
          class="flex-1 px-3 py-2 text-xs font-medium leading-tight text-center rounded-md transition-all duration-200"
          :class="[
            state.matchType === 'exact'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.matchType = 'exact'"
        >
          {{ t('CAMPAIGN.QRBUHO.MATCH_MODE_OPTIONS.EXACT') }}
        </button>
      </div>
      <p class="text-xs text-n-slate-9">
        {{ matchModeDescription }}
      </p>
    </div>

    <!-- Excluir etiquetas (oculto para coincidencia exacta porque ya restringe a esas etiquetas) -->
    <div
      v-if="!isFileAudience && state.matchType !== 'exact'"
      class="flex flex-col gap-1"
    >
      <label
        for="excluded-audience"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.QRBUHO.EXCLUDED_AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        id="excluded-audience"
        :model-value="state.excludedAudience"
        :options="excludedAudienceOptions"
        :placeholder="t('CAMPAIGN.QRBUHO.EXCLUDED_AUDIENCE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="handleExcludedAudienceUpdate"
      />
      <p class="text-xs text-n-slate-9">
        {{ excludedAudienceDescription }}
      </p>
    </div>

    <!-- Fecha y hora programada -->
    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.QRBUHO.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Botones -->
    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.QRBUHO.CREATE.CANCEL_BUTTON_TEXT')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="submitButtonLabel"
        class="w-full"
        type="submit"
        :is-loading="isSubmitting"
        :disabled="isSubmitting || isSubmitDisabled || isGeneratingVariations"
      />
    </div>
  </form>
</template>
